import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  ArrowLeft, BarChart3, TrendingUp, Award, Building2, Activity,
  Calendar, Flame,
} from 'lucide-react';

export default function AdminAnalyticsPage() {
  const { lang } = useApp();
  const [adv, setAdv] = useState(null);

  useEffect(() => {
    api.get('/admin/analytics/advanced').then(r => setAdv(r.data)).catch(() => setAdv({}));
  }, []);

  if (!adv) return <main id="main" className="p-8" data-testid="analytics-loading">{lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}</main>;

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-analytics-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/admin"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'لوحة الإدارة' : 'Admin'}</Link>
      </Button>

      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-amber-100 grid place-items-center"><BarChart3 className="w-6 h-6 text-amber-700" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'التحليلات المتقدّمة' : 'Advanced analytics'}</h1>
          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'اتجاهات أداء، خرائط حرارية للحضور، ومقارنات بين المراكز.' : 'Performance trends, attendance heatmaps & center comparisons.'}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Assessment trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="w-5 h-5 text-primary" />{lang === 'ar' ? 'اتجاه التقييمات (٣٠ يوماً)' : 'Assessments trend (30 days)'}</CardTitle>
          </CardHeader>
          <CardContent>
            {(adv.trend_assessments || []).length === 0 ? (
              <Empty label={lang === 'ar' ? 'لا توجد تقييمات حديثة' : 'No recent assessments'} />
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <AreaChart data={adv.trend_assessments}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14532D" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#14532D" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#B45309" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#B45309" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="count" stroke="#14532D" strokeWidth={2} fill="url(#g1)" name={lang === 'ar' ? 'العدد' : 'Count'} />
                    <Area type="monotone" dataKey="avg_score" stroke="#B45309" strokeWidth={2} fill="url(#g2)" name={lang === 'ar' ? 'متوسط الدرجة' : 'Avg score'} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mastery distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Award className="w-5 h-5 text-emerald-600" />{lang === 'ar' ? 'توزّع الإتقان' : 'Mastery distribution'}</CardTitle>
          </CardHeader>
          <CardContent>
            {(adv.mastery_distribution || []).length === 0 ? (
              <Empty label={lang === 'ar' ? 'لا بيانات' : 'No data'} />
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={adv.mastery_distribution}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#16A34A" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Center comparison */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-cyan-700" />{lang === 'ar' ? 'مقارنة المراكز' : 'Center comparison'}</CardTitle>
          </CardHeader>
          <CardContent>
            {(adv.centers_comparison || []).length === 0 ? (
              <Empty label={lang === 'ar' ? 'لا مراكز' : 'No centers'} />
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={adv.centers_comparison}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="center" tick={{ fontSize: 10 }} angle={-12} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="students" fill="#0E7490" name={lang === 'ar' ? 'الطلاب' : 'Students'} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avg_points" fill="#B45309" name={lang === 'ar' ? 'متوسط النقاط' : 'Avg points'} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Certificate trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Award className="w-5 h-5 text-amber-600" />{lang === 'ar' ? 'إصدار الشهادات' : 'Certificates issued'}</CardTitle>
          </CardHeader>
          <CardContent>
            {(adv.certificate_trend || []).length === 0 ? (
              <Empty label={lang === 'ar' ? 'لا توجد شهادات حديثة' : 'No recent certificates'} />
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={adv.certificate_trend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#B45309" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attendance heatmap */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Flame className="w-5 h-5 text-rose-600" />{lang === 'ar' ? 'خريطة الحضور (٩٠ يوماً)' : 'Attendance heatmap (90 days)'}</CardTitle>
        </CardHeader>
        <CardContent>
          <AttendanceHeatmap data={adv.attendance_heatmap || []} />
        </CardContent>
      </Card>
    </main>
  );
}

function Empty({ label }) {
  return <p className="text-center text-muted-foreground py-10 text-sm">{label}</p>;
}

function AttendanceHeatmap({ data }) {
  const { lang } = useApp();
  const days = lang === 'ar' ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // We'll group cells by [weekday 0..6, hour 0..23]
  const matrix = {};
  let max = 0;
  for (const cell of data) {
    matrix[`${cell.weekday}-${cell.hour}`] = cell.count;
    if (cell.count > max) max = cell.count;
  }
  if (max === 0) return <Empty label={lang === 'ar' ? 'لا توجد بيانات حضور' : 'No attendance data'} />;

  return (
    <div className="overflow-x-auto" data-testid="attendance-heatmap">
      <table className="text-[10px] w-full" cellSpacing={2}>
        <thead>
          <tr>
            <th className="w-12"></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="text-muted-foreground font-normal text-center w-6">{h % 3 === 0 ? h : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d, wd) => (
            <tr key={wd}>
              <td className="font-bold text-muted-foreground pe-2">{d}</td>
              {Array.from({ length: 24 }, (_, h) => {
                const count = matrix[`${wd}-${h}`] || 0;
                const intensity = count / max;
                const bg = count === 0 ? '#F3F4F6' : `rgba(20, 83, 45, ${0.15 + intensity * 0.85})`;
                return (
                  <td key={h}>
                    <div title={`${d} ${h}:00 — ${count}`} style={{ background: bg }} className="w-6 h-5 rounded-sm" />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
        <span>{lang === 'ar' ? 'أقل' : 'Less'}</span>
        {[0.15, 0.35, 0.55, 0.75, 1].map((a, i) => (
          <div key={i} style={{ background: `rgba(20, 83, 45, ${a})` }} className="w-4 h-4 rounded-sm" />
        ))}
        <span>{lang === 'ar' ? 'أكثر' : 'More'}</span>
      </div>
    </div>
  );
}
